import {
    Alert,
    AlertColor,
    AlignItems,
    Badge,
    BadgeColor,
    Button,
    ButtonColor,
    Card,
    Container,
    ContainerSize,
    Dropdown,
    Gap,
    Heading,
    JustifyContent,
    Modal,
    Nav,
    Navbar,
    Pagination,
    PaginationSize,
    Popover,
    Stack,
    StackDirection,
    StackWrap,
    Tabs,
    Text,
    TooltipPlacement,
    Form,
} from "@rebootui";
import CodePreview from "../controls/CodePreview";
import CodeHighlighter from "../controls/CodeHighlighter";
import CodeSnippet from "../controls/CodeSnippet";
import reactImage from "../../assets/images/reactLogo.png";
import React from "react";
import { Link } from "react-router-dom";

type ComponentDemo = {
    id: string;
    label: string;
    /** Full documentation page for this example */
    docsHref: string;
    code: string;
    preview: React.ReactNode;
};

const Components = () => {
    const [activeModal, setActiveModal] = React.useState<string | null>(null);

    const openModal = React.useCallback((modalName: string) => {
        setActiveModal(modalName);
    }, []);

    const closeModal = React.useCallback(() => {
        setActiveModal(null);
    }, []);

    const isModalOpen = React.useCallback(
        (modalName: string) => activeModal === modalName,
        [activeModal]
    );

    const components: ComponentDemo[] = React.useMemo(
        () => [
            {
                id: "alert",
                label: "Alerts",
                docsHref: "/docs/components/alert",
                code: alertCode,
                preview: (
                    <Stack direction={StackDirection.Column}>
                        <Alert color={AlertColor.Primary}>
                            A simple primary alert—check it out!
                        </Alert>
                        <Alert color={AlertColor.Success}>
                            A simple Success alert—check it out!
                        </Alert>
                    </Stack>
                ),
            },
            {
                id: "button",
                label: "Buttons",
                docsHref: "/docs/components/button",
                code: buttonCode,
                preview: (
                    <Stack
                        direction={StackDirection.Row}
                        gap={Gap.Gap2}
                        wrap={StackWrap.Wrap}
                    >
                        <Button color={ButtonColor.Primary}>Primary</Button>
                        <Button color={ButtonColor.Secondary}>Secondary</Button>
                        <Button color={ButtonColor.Success}>Success</Button>
                        <Button color={ButtonColor.Danger}>Danger</Button>
                        <Button color={ButtonColor.Warning}>Warning</Button>
                        <Button color={ButtonColor.Info}>Info</Button>
                        <Button color={ButtonColor.Light}>Light</Button>
                        <Button color={ButtonColor.Dark}>Dark</Button>
                    </Stack>
                ),
            },
            {
                id: "badge",
                label: "Badges",
                docsHref: "/docs/components/badge",
                code: badgeCode,
                preview: (
                    <Stack
                        direction={StackDirection.Row}
                        gap={Gap.Gap2}
                        wrap={StackWrap.Wrap}
                        alignItems={AlignItems.Center}
                    >
                        <Badge color={BadgeColor.Primary}>Primary</Badge>
                        <Badge color={BadgeColor.Secondary}>Secondary</Badge>
                        <Badge color={BadgeColor.Success}>Success</Badge>
                        <Badge color={BadgeColor.Danger}>Danger</Badge>
                        <Badge color={BadgeColor.Warning}>Warning</Badge>
                        <Badge color={BadgeColor.Info}>Info</Badge>
                        <Badge color={BadgeColor.Light}>Light</Badge>
                        <Badge color={BadgeColor.Dark}>Dark</Badge>
                    </Stack>
                ),
            },
            {
                id: "card",
                label: "Cards",
                docsHref: "/docs/components/card",
                code: cardCode,
                preview: (
                    <Stack direction={StackDirection.Row} gap={Gap.Gap2} wrap={StackWrap.Wrap}>
                        <Card style={{ width: '18rem' }}>
                            <Card.Image src={reactImage} />
                            <Card.Body>
                                <Card.Text>Some quick example text to build on the card title and make up the bulk of the card's content.</Card.Text>
                            </Card.Body>
                        </Card>

                        <Card style={{ width: '18rem' }}>
                            <Card.Body>
                                <Card.Text>Some quick example text to build on the card title and make up the bulk of the card's content.</Card.Text>
                            </Card.Body>
                            <Card.Image src={reactImage} />
                        </Card>
                    </Stack>
                ),
            },
            {
                id: "dropdown",
                label: "Dropdown",
                docsHref: "/docs/components/dropdown",
                code: dropdownCode,
                preview: (
                   <Stack direction={StackDirection.Row} gap={Gap.Gap2} wrap={StackWrap.Wrap}>
                    <Dropdown>
                        <Dropdown.Toggle color={ButtonColor.Primary}>Dropdown Button</Dropdown.Toggle>
                        <Dropdown.Menu>
                            <Dropdown.Item el="a" href="#">Action</Dropdown.Item>
                            <Dropdown.Item el="a" href="#">Another action</Dropdown.Item>
                            <Dropdown.Item el="a" href="#">Something else here</Dropdown.Item>
                        </Dropdown.Menu>
                    </Dropdown>
                    <Dropdown>
                        <Dropdown.Toggle color={ButtonColor.Secondary}>Dropdown Button</Dropdown.Toggle>
                        <Dropdown.Menu>
                            <Dropdown.Item el="a" href="#">Action</Dropdown.Item>
                            <Dropdown.Item el="a" href="#">Another action</Dropdown.Item>
                            <Dropdown.Item el="a" href="#">Something else here</Dropdown.Item>
                        </Dropdown.Menu>
                    </Dropdown>
                   </Stack>
                ),
            },
            {
                id: "modal",
                label: "Modal",
                docsHref: "/docs/components/modal",
                code: modalCode,
                preview: (
                    <>
                        <Button
                            color={ButtonColor.Primary}
                            onClick={() => openModal("basic")}
                        >
                            Launch demo modal
                        </Button>
                        <Modal show={isModalOpen("basic")} onClose={closeModal}>
                            <Modal.Dialog>
                                <Modal.Header>
                                    <Heading el="h5" className="modal-title">
                                        Modal Title
                                    </Heading>
                                    <Button
                                        type="button"
                                        className="btn-close"
                                        onClick={closeModal}
                                    >
                                        &nbsp;
                                    </Button>
                                </Modal.Header>
                                <Modal.Body>
                                    <Text el="p">
                                        This is the modal body content.
                                    </Text>
                                </Modal.Body>
                                <Modal.Footer>
                                    <Button
                                        color={ButtonColor.Secondary}
                                        onClick={closeModal}
                                    >
                                        Close
                                    </Button>
                                    <Button color={ButtonColor.Primary}>
                                        Save changes
                                    </Button>
                                </Modal.Footer>
                            </Modal.Dialog>
                        </Modal>
                    </>
                ),
            },
            {
                id: "navigation",
                label: "Navigation",
                docsHref: "/docs/components/navbar",
                code: navbarCode,
                preview: (
                    <Navbar className="navbar-expand-lg bg-body-tertiary">
                        <Container size={ContainerSize.Fluid}>
                            <Navbar.Brand>Navbar</Navbar.Brand>
                            <Navbar.Toggle />
                            <Navbar.Collapse>
                                <Nav className="me-auto mb-2 mb-lg-0">
                                    <Nav.Item>
                                        <Nav.Link to="#" active>
                                            Home
                                        </Nav.Link>
                                    </Nav.Item>
                                    <Nav.Item>
                                        <Nav.Link to="/">Features</Nav.Link>
                                    </Nav.Item>
                                    <Nav.Item>
                                        <Nav.Link to="/">Pricing</Nav.Link>
                                    </Nav.Item>
                                    <Nav.Item>
                                        <Nav.Link to="/" disabled>
                                            Disabled
                                        </Nav.Link>
                                    </Nav.Item>
                                </Nav>
                                <Form className="w-auto">
                                    <Stack direction={StackDirection.Row} gap={Gap.Gap2} wrap={StackWrap.Wrap} alignItems={AlignItems.Center}>
                                        <Form.Control className="w-auto" type="search" placeholder="Search" aria-label="Search" />
                                        <Button color={ButtonColor.OutlineSuccess} type="submit">Search</Button>
                                    </Stack>
                                </Form>
                            </Navbar.Collapse>
                        </Container>
                    </Navbar>
                ),
            },
            {
                id: "pagination",
                label: "Pagination",
                docsHref: "/docs/components/pagination",
                code: paginationCode,
                preview: (
                    <Pagination size={PaginationSize.Sm}>
                        <Pagination.Item to="#">Previous</Pagination.Item>
                        <Pagination.Item to="#">1</Pagination.Item>
                        <Pagination.Item to="#" isActive={true}>
                            2
                        </Pagination.Item>
                        <Pagination.Item to="#">3</Pagination.Item>
                        <Pagination.Item to="#">Next</Pagination.Item>
                    </Pagination>
                ),
            },
            {
                id: "popover",
                label: "Popover",
                docsHref: "/docs/components/popover",
                code: popoverCode,
                preview: (
                    <Stack
                        direction={StackDirection.Row}
                        gap={Gap.Gap2}
                        wrap={StackWrap.Wrap}
                    >
                        <Popover 
                            container="inline"
                            title="Top Popover"
                            content="This popover appears on top."
                            placement={TooltipPlacement.Top}
                        >
                            <Button color={ButtonColor.Primary}>
                                Popover on top
                            </Button>
                        </Popover>
                        <Popover
                            container="inline"
                            title="End Popover"
                            content="This popover appears on the end (right)."
                            placement={TooltipPlacement.End}
                        >
                            <Button color={ButtonColor.Primary}>
                                Popover on end
                            </Button>
                        </Popover>
                        <Popover
                            container="inline"
                            title="Bottom Popover"
                            content="This popover appears on bottom."
                            placement={TooltipPlacement.Bottom}
                        >
                            <Button color={ButtonColor.Primary}>
                                Popover on bottom
                            </Button>
                        </Popover>
                        <Popover
                            container="inline"
                            title="Start Popover"
                            content="This popover appears on start (left)."
                            placement={TooltipPlacement.Start}
                        >
                            <Button color={ButtonColor.Primary}>
                                Popover on start
                            </Button>
                        </Popover>
                    </Stack>
                ),
            },
        ],
        [closeModal, isModalOpen, openModal]
    );

    return (
        <section className="py-md-7 py-6" aria-labelledby="try-components-heading">
            <Container>
                <div className="text-center mb-6">
                    <Text id="try-components-heading" className="h2 mb-3">
                        Try components live
                    </Text>
                    <Text className="fs-5 mb-2 text-secondary">
                        Pick a component, interact with the preview, then copy
                        the code or open the full docs for props and patterns.
                    </Text>
                    <Text className="small text-secondary mb-0">
                        On small screens, swipe the pill tabs horizontally.
                    </Text>
                </div>
                <Tabs defaultActiveKey="alert">
                    <Nav
                        role="tablist"
                        aria-label="Component examples"
                        className="nav nav-pills flex-sm-wrap flex-nowrap overflow-x-auto justify-content-sm-center gap-2 pb-1"
                    >
                        {components.map((comp) => (
                            <Nav.Item key={comp.id} role="presentation">
                                <Nav.Link
                                    eventKey={comp.id}
                                    className="rounded-pill px-4 lh-180 text-nowrap"
                                    id={`tab-${comp.id}`}
                                    aria-controls={`panel-${comp.id}`}
                                >
                                    {comp.label}
                                </Nav.Link>
                            </Nav.Item>
                        ))}
                    </Nav>
                    <Tabs.Content className="mt-3">
                        <Stack
                            direction={StackDirection.Column}
                            justifyContent={JustifyContent.Center}
                            alignItems={AlignItems.Center}
                            className="p-3 w-100 rounded-5 border border-black border-opacity-5 mt-xl overflow-hidden"
                        >
                            {components.map((comp) => (
                                <Tabs.Pane
                                    eventKey={comp.id}
                                    className="w-100"
                                    key={comp.id}
                                    id={`panel-${comp.id}`}
                                    aria-labelledby={`tab-${comp.id}`}
                                >
                                    <div className="w-100">
                                        <div className="d-flex flex-column flex-sm-row align-items-start align-items-sm-center justify-content-between gap-2 px-3 pt-3">
                                            <Text
                                                className="small fw-semibold text-uppercase text-secondary mb-0"
                                                style={{ letterSpacing: "0.06em" }}
                                            >
                                                {comp.label}
                                            </Text>
                                            <Link
                                                to={comp.docsHref}
                                                className="small fw-semibold text-decoration-none"
                                            >
                                                Full documentation →
                                            </Link>
                                        </div>
                                        <Text className="small text-secondary px-3 mb-2 mt-1">
                                            Live preview
                                        </Text>
                                        <CodeSnippet className="rounded-4 border-opacity-25 border-secondary overflow-hidden">
                                            <CodePreview>
                                                {comp.preview}
                                            </CodePreview>
                                            <CodeHighlighter language="tsx">
                                                {comp.code}
                                            </CodeHighlighter>
                                        </CodeSnippet>
                                    </div>
                                </Tabs.Pane>
                            ))}
                        </Stack>
                    </Tabs.Content>
                </Tabs>
                <Text className="text-center mt-5 lh-120 small mb-0 text-secondary">
                    Fully responsive · Accessible · Customizable
                </Text>
            </Container>
        </section>
    );
};

export default Components;

const buttonCode = `<Stack direction={StackDirection.Row} gap={Gap.Gap2} wrap={StackWrap.Wrap}>
    <Button color={ButtonColor.Primary}>Primary</Button>
    <Button color={ButtonColor.Secondary}>Secondary</Button>
    <Button color={ButtonColor.Success}>Success</Button>
    <Button color={ButtonColor.Danger}>Danger</Button>
    <Button color={ButtonColor.Warning}>Warning</Button>
    <Button color={ButtonColor.Info}>Info</Button>
    <Button color={ButtonColor.Light}>Light</Button>
    <Button color={ButtonColor.Dark}>Dark</Button>
</Stack>`;

const alertCode = `<Stack direction={StackDirection.Column}>
    <Alert color={AlertColor.Primary}>
        A simple primary alert—check it out!
    </Alert>
    <Alert color={AlertColor.Success}>
        A simple Success alert—check it out!
    </Alert>
</Stack>`;

const badgeCode = `<Stack direction={StackDirection.Row} gap={Gap.Gap2} wrap={StackWrap.Wrap} alignItems={AlignItems.Center}>
    <Badge color={BadgeColor.Primary}>Primary</Badge>
    <Badge color={BadgeColor.Secondary}>Secondary</Badge>
    <Badge color={BadgeColor.Success}>Success</Badge>
    <Badge color={BadgeColor.Danger}>Danger</Badge>
    <Badge color={BadgeColor.Warning}>Warning</Badge>
    <Badge color={BadgeColor.Info}>Info</Badge>
    <Badge color={BadgeColor.Light}>Light</Badge>
    <Badge color={BadgeColor.Dark}>Dark</Badge>
</Stack>`;

const cardCode = `<Stack direction={StackDirection.Row} gap={Gap.Gap2} wrap={StackWrap.Wrap}>
    <Card style={{ width: '18rem' }}>
        <Card.Image src={reactImage} />
        <Card.Body>
            <Card.Text>Some quick example text to build on the card title and make up the bulk of the card's content.</Card.Text>
        </Card.Body>
    </Card>

    <Card style={{ width: '18rem' }}>
        <Card.Body>
            <Card.Text>Some quick example text to build on the card title and make up the bulk of the card's content.</Card.Text>
        </Card.Body>
        <Card.Image src={reactImage} />
    </Card>
</Stack>`;

const dropdownCode = `<Stack direction={StackDirection.Row} gap={Gap.Gap2} wrap={StackWrap.Wrap}>
    <Dropdown>
        <Dropdown.Toggle color={ButtonColor.Primary}>Dropdown Button</Dropdown.Toggle>
        <Dropdown.Menu>
            <Dropdown.Item el="a" href="#">Action</Dropdown.Item>
            <Dropdown.Item el="a" href="#">Another action</Dropdown.Item>
            <Dropdown.Item el="a" href="#">Something else here</Dropdown.Item>
        </Dropdown.Menu>
    </Dropdown>
    <Dropdown>
        <Dropdown.Toggle color={ButtonColor.Secondary}>Dropdown Button</Dropdown.Toggle>
        <Dropdown.Menu>
            <Dropdown.Item el="a" href="#">Action</Dropdown.Item>
            <Dropdown.Item el="a" href="#">Another action</Dropdown.Item>
            <Dropdown.Item el="a" href="#">Something else here</Dropdown.Item>
        </Dropdown.Menu>
    </Dropdown>
</Stack>`;

const modalCode = `const [activeModal, setActiveModal] = React.useState<string | null>(null);

const openModal = (modalName: string) => setActiveModal(modalName);
const closeModal = () => setActiveModal(null);
const isModalOpen = (modalName: string) => activeModal === modalName;

<Button color={ButtonColor.Primary} onClick={() => openModal('basic')}>Launch demo modal</Button>

<Modal show={isModalOpen('basic')} onClose={closeModal}>
    <Modal.Dialog>
        <Modal.Header>
            <Heading el="h5" className="modal-title">Modal Title</Heading>
            <Button type="button" className="btn-close" onClick={closeModal}>&nbsp;</Button>
        </Modal.Header>
        <Modal.Body>
            <Text el="p">This is the modal body content.</Text>
        </Modal.Body>
        <Modal.Footer>
            <Button color={ButtonColor.Secondary} onClick={closeModal}>Close</Button>
            <Button color={ButtonColor.Primary}>Save changes</Button>
        </Modal.Footer>
    </Modal.Dialog>
</Modal>`;

const navbarCode = `<Navbar className="navbar-expand-lg bg-body-tertiary">
    <Container size={ContainerSize.Fluid}>
        <Navbar.Brand>Navbar</Navbar.Brand>
        <Navbar.Toggle />
        <Navbar.Collapse>
            <Nav className="me-auto mb-2 mb-lg-0">
                <Nav.Item>
                    <Nav.Link to="#" active>Home</Nav.Link>
                </Nav.Item>
                <Nav.Item>
                    <Nav.Link to="/">Features</Nav.Link>
                </Nav.Item>
                <Nav.Item>
                    <Nav.Link to="/">Pricing</Nav.Link>
                </Nav.Item>
                <Nav.Item>
                    <Nav.Link to="/" disabled>Disabled</Nav.Link>
                </Nav.Item>
            </Nav>
            <Form className="w-auto">
                <Stack direction={StackDirection.Row} gap={Gap.Gap2} wrap={StackWrap.Wrap} alignItems={AlignItems.Center}>
                    <Form.Control className="w-auto" type="search" placeholder="Search" aria-label="Search" />
                    <Button color={ButtonColor.OutlineSuccess} type="submit">Search</Button>
                </Stack>
            </Form>
        </Navbar.Collapse>
    </Container>
</Navbar>`;

const paginationCode = `<Pagination size={PaginationSize.Sm}>
    <Pagination.Item to="#">Previous</Pagination.Item>
    <Pagination.Item to="#">1</Pagination.Item>
    <Pagination.Item to="#" isActive={true}>2</Pagination.Item>
    <Pagination.Item to="#">3</Pagination.Item>
    <Pagination.Item to="#">Next</Pagination.Item>
</Pagination>`;

const popoverCode = `<Stack direction={StackDirection.Row} gap={Gap.Gap2} wrap={StackWrap.Wrap}>
    <Popover container="inline" title="Top Popover" content="This popover appears on top." placement={TooltipPlacement.Top}>
        <Button color={ButtonColor.Primary}>Popover on top</Button>
    </Popover>
    <Popover container="inline" title="End Popover" content="This popover appears on the end (right)." placement={TooltipPlacement.End}>
        <Button color={ButtonColor.Primary}>Popover on end</Button>
    </Popover>
    <Popover container="inline" title="Bottom Popover" content="This popover appears on bottom." placement={TooltipPlacement.Bottom}>
        <Button color={ButtonColor.Primary}>Popover on bottom</Button>
    </Popover>
    <Popover container="inline" title="Start Popover" content="This popover appears on start (left)." placement={TooltipPlacement.Start}>
        <Button color={ButtonColor.Primary}>Popover on start</Button>
    </Popover>
</Stack>`;
